import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { TourStepConfig, ArrowPlacement } from './tour-steps';
import { CloseCircleGreyIcon } from 'assets';

interface TourStepProps {
  step: TourStepConfig;
  onNext: () => void;
  onSkip: () => void;
  onOkay: () => void;
  onDone: () => void;
}

function getArrowStyle(placement: ArrowPlacement): React.CSSProperties {
  const base: React.CSSProperties = { transform: 'rotate(45deg)', boxShadow: '-1px -1px 3px rgba(0,0,0,0.06)' };
  switch (placement) {
    case 'top-left': return { ...base, top: -7, left: 20 };
    case 'top-center': return { ...base, top: -7, left: '50%', transform: 'translateX(-50%) rotate(45deg)' };
    case 'top-right': return { ...base, top: -7, right: 20 };
    case 'bottom-left': return { ...base, bottom: -7, left: 20, boxShadow: '1px 1px 3px rgba(0,0,0,0.06)' };
    case 'bottom-center': return { ...base, bottom: -7, left: '50%', transform: 'translateX(-50%) rotate(45deg)', boxShadow: '1px 1px 3px rgba(0,0,0,0.06)' };
    case 'bottom-right': return { ...base, bottom: -7, right: 20, boxShadow: '1px 1px 3px rgba(0,0,0,0.06)' };
    case 'right-top': return { ...base, top: 20, right: -7, boxShadow: '1px -1px 3px rgba(0,0,0,0.06)' };
    case 'left-top': return { ...base, top: 20, left: -7, boxShadow: '-1px 1px 3px rgba(0,0,0,0.06)' };
    default: return base;
  }
}

interface CardPosition {
  top: number;
  left: number;
  arrowLeft?: number;
  arrowTop?: number;
}

function getScrollParents(element: HTMLElement): (HTMLElement | Window)[] {
  const parents: (HTMLElement | Window)[] = [];
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const styles = window.getComputedStyle(current);
    const overflowX = styles.overflowX;
    const overflowY = styles.overflowY;
    if (/(auto|scroll|overlay)/.test(overflowX) || /(auto|scroll|overlay)/.test(overflowY)) {
      parents.push(current);
    }
    current = current.parentElement;
  }

  parents.push(window);
  return parents;
}

function computePosition(
  targetRect: DOMRect,
  placement: ArrowPlacement,
  cardWidth: number,
  cardHeight: number
): CardPosition {
  const gap = 16;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top-left':
      top = targetRect.bottom + gap;
      left = targetRect.left;
      break;
    case 'top-center':
      top = targetRect.bottom + gap;
      left = targetRect.left + targetRect.width / 2 - cardWidth / 2;
      break;
    case 'top-right':
      top = targetRect.bottom + gap;
      left = targetRect.right - cardWidth;
      break;
    case 'bottom-left':
      top = targetRect.top - cardHeight - gap;
      left = targetRect.left;
      break;
    case 'bottom-center':
      top = targetRect.top - cardHeight - gap;
      left = targetRect.left + targetRect.width / 2 - cardWidth / 2;
      break;
    case 'bottom-right':
      top = targetRect.top - cardHeight - gap;
      left = targetRect.right - cardWidth;
      break;
    case 'right-top':
      top = targetRect.top;
      left = targetRect.left - cardWidth - gap;
      break;
    case 'left-top':
      top = targetRect.top;
      left = targetRect.right + gap;
      break;
    default:
      top = vh / 2 - cardHeight / 2;
      left = vw / 2 - cardWidth / 2;
  }

  top = Math.max(16, Math.min(top, vh - cardHeight - 16));
  left = Math.max(16, Math.min(left, vw - cardWidth - 16));

  return { top, left };
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
  radius: string;
}

const TourStep: React.FC<TourStepProps> = ({ step, onNext, onSkip, onOkay, onDone }) => {
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [cardPos, setCardPos] = useState<CardPosition | null>(null);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);
  const isModal = step.type === 'modal' || !step.targetId;
  const arrowInset = 25;

  useEffect(() => {
    if (isModal) return;

    const selector = `[data-tour-id="${step.targetId}"]`;
    const el = document.querySelector<HTMLElement>(selector);
    if (el) {
      setTargetEl(el);
      return;
    }

    const observer = new MutationObserver(() => {
      const found = document.querySelector<HTMLElement>(selector);
      if (found) {
        setTargetEl(found);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [step.targetId, isModal]);

  const updatePosition = useCallback(() => {
    if (!cardRef.current) return;

    const selector = step.targetId ? `[data-tour-id="${step.targetId}"]` : null;
    const liveTarget = selector ? document.querySelector<HTMLElement>(selector) : targetEl;
    if (!liveTarget) return;

    const targetRect = liveTarget.getBoundingClientRect();

    // Cut a highlight "hole" that hugs the target exactly (no padding) and
    // mirrors its own border-radius so the cutout blends with the element.
    const radius = window.getComputedStyle(liveTarget).borderRadius || '0px';
    setSpotlight(prev => {
      const next: SpotlightRect = {
        top: targetRect.top,
        left: targetRect.left,
        width: targetRect.width,
        height: targetRect.height,
        radius,
      };
      if (
        prev?.top === next.top && prev?.left === next.left &&
        prev?.width === next.width && prev?.height === next.height &&
        prev?.radius === next.radius
      ) return prev;
      return next;
    });

    const cardRect = cardRef.current.getBoundingClientRect();
    const cardWidth = step.tooltipWidth ?? 320;
    const cardHeight = cardRect.height || 180;

    const pos = computePosition(targetRect, step.arrowPlacement, cardWidth, cardHeight);
    const cardTop = pos.top + (step.offsetY ?? 0);
    const cardLeft = pos.left + (step.offsetX ?? 0);

    const placement = step.arrowPlacement;
    let arrowLeft: number | undefined;
    let arrowTop: number | undefined;

    if (['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'].includes(placement)) {
      const targetCenterX = targetRect.left + targetRect.width / 2;
      arrowLeft = Math.max(arrowInset, Math.min(targetCenterX - cardLeft, cardWidth - arrowInset));
    } else if (placement === 'left-top' || placement === 'right-top') {
      const targetCenterY = targetRect.top + targetRect.height / 2;
      arrowTop = Math.max(arrowInset, Math.min(targetCenterY - cardTop, cardHeight - arrowInset));
    }

    setCardPos(prev => {
      if (
        prev?.top === cardTop && prev?.left === cardLeft &&
        prev?.arrowLeft === arrowLeft && prev?.arrowTop === arrowTop
      ) return prev;
      return { top: cardTop, left: cardLeft, arrowLeft, arrowTop };
    });
  }, [targetEl, step.arrowPlacement, step.targetId, step.tooltipWidth, step.offsetX, step.offsetY]);

  useEffect(() => {
    if (!targetEl) return;

    const targetRect = targetEl.getBoundingClientRect();
    const isOffscreen =
      targetRect.top < 16 ||
      targetRect.left < 16 ||
      targetRect.bottom > window.innerHeight - 16 ||
      targetRect.right > window.innerWidth - 16;

    if ((step.autoScrollIntoView || isOffscreen) && !hasAutoScrolledRef.current) {
      hasAutoScrolledRef.current = true;
      targetEl.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      window.requestAnimationFrame(updatePosition);
      return;
    }

    updatePosition();

    const ro = new ResizeObserver(updatePosition);
    ro.observe(targetEl);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [targetEl, updatePosition]);

  useLayoutEffect(() => {
    if (!targetEl) return;

    const frameOne = window.requestAnimationFrame(updatePosition);
    const frameTwo = window.requestAnimationFrame(updatePosition);

    return () => {
      window.cancelAnimationFrame(frameOne);
      window.cancelAnimationFrame(frameTwo);
    };
  }, [targetEl, updatePosition]);

  useEffect(() => {
    if (!targetEl) return;

    const scrollParents = getScrollParents(targetEl);
    const resizeObservers = scrollParents
      .filter((parent): parent is HTMLElement => parent instanceof HTMLElement)
      .map((parent) => {
        const observer = new ResizeObserver(updatePosition);
        observer.observe(parent);
        return observer;
      });

    scrollParents.forEach((parent) => {
      parent.addEventListener('scroll', updatePosition, { passive: true } as AddEventListenerOptions);
    });

    return () => {
      scrollParents.forEach((parent) => {
        parent.removeEventListener('scroll', updatePosition);
      });
      resizeObservers.forEach((observer) => observer.disconnect());
    };
  }, [targetEl, updatePosition]);

  useEffect(() => {
    hasAutoScrolledRef.current = false;
    setSpotlight(null);
  }, [step.targetId]);

  useEffect(() => {
    if (targetEl && cardRef.current) {
      updatePosition();
    }
  }, [targetEl, updatePosition]);

  // Images/GIFs change the card's height once they finish loading. Recompute the
  // position after they settle so a slow asset doesn't leave the card distorted.
  const handleMediaSettled = useCallback(() => {
    window.requestAnimationFrame(updatePosition);
  }, [updatePosition]);

  const cardStyle: React.CSSProperties = isModal
    ? {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: step.type === 'modal' ? 460 : (step.tooltipWidth ?? 320),
      maxWidth: 'calc(100vw - 32px)',
    }
    : cardPos
      ? {
        top: cardPos.top,
        left: cardPos.left,
        width: step.tooltipWidth ?? 320,
        maxWidth: 'calc(100vw - 32px)',
      }
      : {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: step.tooltipWidth ?? 320,
        maxWidth: 'calc(100vw - 32px)',
        opacity: 0,
        pointerEvents: 'none',
      };

  const showArrow = !isModal && step.arrowPlacement !== 'none' && cardPos !== null;

  const dimColor = 'rgba(36,42,51,0.5)';

  const overlay = !isModal && spotlight ? (
    <>
      {/* Transparent layer keeps page interaction blocked during the tour. */}
      <div className="fixed inset-0 z-[1200]" />
      {/* Spotlight: dims everything except the target via a large box-shadow,
          so it clearly pops above the overlay. */}
      <div
        className="fixed z-[1200] pointer-events-none transition-all duration-200"
        style={{
          top: spotlight.top,
          left: spotlight.left,
          width: spotlight.width,
          height: spotlight.height,
          borderRadius: spotlight.radius,
          boxShadow: `0 0 0 9999px ${dimColor}`,
        }}
      />
    </>
  ) : (
    <div className="fixed inset-0 z-[1200]" style={{ background: dimColor }} />
  );

  const content = (
    <>
      {overlay}

      <div
        ref={cardRef}
        className="fixed z-[1201] bg-white rounded-2xl px-8 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.18)]"
        style={cardStyle}
      >
        {showArrow && (
          <div
            className="absolute w-[14px] h-[14px] bg-white"
            style={{
              ...getArrowStyle(step.arrowPlacement),
              ...(step.arrowOffsetX != null
                ? { left: step.arrowOffsetX }
                : cardPos?.arrowLeft != null
                  ? { left: cardPos.arrowLeft }
                  : {}),
              ...(cardPos?.arrowTop != null ? { top: cardPos.arrowTop } : {}),
            }}
          />
        )}

        <div className="relative pt-4">
          <div className="text-xs text-[#1570EF] text-center tracking-[0.02em] bg-[#EFF8FF] py-1 px-3 rounded-full w-max mx-auto">
            Product tour
          </div>

          <button
            className="absolute top-0 -right-3 bg-transparent border-0 cursor-pointer text-[#667085] p-0 leading-none text-lg flex items-center justify-center w-6 h-6 rounded-full hover:text-[#344054] hover:bg-[#f2f4f7]"
            onClick={onSkip}
            aria-label="Close tour"
          >
            <CloseCircleGreyIcon />
          </button>

          <h2 className="text-2xl font-bold text-[#012169] text-center mb-2 mt-1 ml-0 leading-snug">
            {step.title}
          </h2>

          <p className="text-base text-[#667085] text-center leading-[1.4] mb-6">
            {step.description}
          </p>

          {step.gif !== undefined && (
            step.gif ? (
              <div className="w-full min-h-[160px] rounded-xl mt-3 mb-4 bg-[#EFF8FF] overflow-hidden">
                <img
                  src={step.gif}
                  alt=""
                  className="w-full rounded-xl"
                  onLoad={handleMediaSettled}
                  onError={handleMediaSettled}
                />
              </div>
            ) : (
              <div className="w-full h-60 bg-[#EFF8FF] rounded-xl mt-3 mb-4 flex items-center justify-center text-[#667085] text-base">
                GIF from the demo video
              </div>
            )
          )}

          {step.media && (
            <div className="w-full min-h-[140px] rounded-xl bg-[#F5FAFF] p-2 overflow-hidden my-4">
              <img
                src={step.media}
                alt=""
                className="w-full rounded-xl "
                onLoad={handleMediaSettled}
                onError={handleMediaSettled}
              />
            </div>

          )}

          {step.buttons === 'skip-next' && (
            <div className="flex gap-3 justify-between items-center mt-8">
              <button
                className="bg-white border border-[#d0d5dd] text-[#344054] text-sm py-2 px-8 rounded-md cursor-pointer font-[inherit] hover:bg-[#f9fafb]"
                onClick={onSkip}
              >
                Skip
              </button>
              <button
                className="bg-[#012169] border-0 text-[#FCFCFD] text-sm font-semibold py-2 px-5 rounded-md cursor-pointer font-[inherit] hover:bg-[#01307a]"
                onClick={onNext}
              >
                {step.nextLabel ?? 'Next'}
              </button>
            </div>
          )}

          {step.buttons === 'okay' && (
            <div className="flex justify-center mt-1">
              <button
                className="bg-[#012169] border-0 text-[#FCFCFD] text-sm py-2 px-14 rounded-md cursor-pointer font-[inherit] hover:bg-[#01307a]"
                onClick={onOkay}
              >
                Okay
              </button>
            </div>
          )}

          {step.buttons === 'done' && (
            <div className="flex justify-center mt-1">
              <button
                className="bg-[#012169] border-0 text-[#FCFCFD] text-sm py-2 px-14 rounded-md cursor-pointer font-[inherit] hover:bg-[#01307a]"
                onClick={onDone}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export { TourStep };
